/*
 * ------------------------------------------------------------------------
 * Hala Gear custom theme behaviour
 * Consolidated 2026-08-19 from five inline <script> blocks in theme.liquid.
 *
 * Loaded once with `defer`, so it is cached across pages instead of being
 * re-downloaded and re-parsed inside every HTML response (~181 KB of inline
 * script per page before this change).
 *
 * Modules, in order:
 *   1. Async country list  (new):      see sections/hg-country-list.liquid
 *   2. About-page video popup:         moved verbatim
 *   3. PDP rating -> reviews scroll:   moved, null-guard added
 *   4. SPICE add-on cart drawer sync:  moved, re-attaches module 5
 *   5. EPA cart drawer price fix:      moved, reformatted, observer survives
 *                                        a drawer refresh
 * ------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ====================================================================
   * 1. ASYNC COUNTRY LIST
   *
   * The country selector markup (~230 countries, each with a flag <img>,
   * rendered in both the header and the footer) is no longer printed into
   * every page. snippets/localization-selector.liquid emits an empty
   * placeholder and we fetch sections/hg-country-list.liquid on demand.
   *
   * We prefetch on pointerenter / focus / touchstart so that by the time
   * the popover actually opens the list is already in the DOM. The one
   * network round trip is shared between the header and footer selectors.
   * ==================================================================== */
  var COUNTRY_SECTION = 'hg-country-list';
  var countryListPromise = null;

  function currentPath() {
    return window.location.pathname + window.location.search;
  }

  function fetchCountryList() {
    if (countryListPromise) return countryListPromise;

    var url =
      window.location.pathname +
      (window.location.search ? window.location.search + '&' : '?') +
      'sections=' +
      COUNTRY_SECTION;

    countryListPromise = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('country list HTTP ' + response.status);
        return response.json();
      })
      .then(function (json) {
        var html = json[COUNTRY_SECTION];
        if (!html) throw new Error('country list section missing from response');
        return html;
      })
      .catch(function (error) {
        // Allow a later interaction to retry rather than failing forever.
        countryListPromise = null;
        throw error;
      });

    return countryListPromise;
  }

  /*
   * Build the <form> in the CURRENT document.
   *
   * Do NOT use DOMParser here. Nodes parsed into a detached document keep the
   * lazy-loading state they were given there, so the 237 country flags stayed
   * permanently unloaded once adopted into the live page: the list rendered
   * with country names but no flags. createContextualFragment parses against
   * the live document, so the flags lazy-load normally as the shopper scrolls
   * the dropdown.
   */
  function buildCountryForm(html) {
    var range = document.createRange();
    range.selectNode(document.body);

    var fragment = range.createContextualFragment(html);

    // The section ships no scripts today; strip any defensively so a future
    // edit to the section cannot start executing once per selector instance.
    fragment.querySelectorAll('script').forEach(function (node) {
      node.parentNode.removeChild(node);
    });

    return fragment.querySelector('form');
  }

  function hydrateCountryList(placeholder) {
    if (!placeholder || placeholder.dataset.hgState) return;
    placeholder.dataset.hgState = 'loading';

    fetchCountryList()
      .then(function (html) {
        // The placeholder can be torn out by a section re-render while we wait.
        if (!placeholder.isConnected) return;

        // Built fresh per placeholder rather than cloned, so each of the three
        // selectors (header, mobile menu, footer) gets its own clean nodes.
        var clone = buildCountryForm(html);
        if (!clone) throw new Error('country list form missing from section');

        // Preserve the per-instance form id so header and footer stay unique.
        var formId = placeholder.getAttribute('data-hg-form-id');
        if (formId) clone.id = formId;

        // The section was fetched from a URL carrying ?sections=..., so make
        // sure Shopify returns the shopper to the real page after the switch.
        var returnTo = clone.querySelector('input[name="return_to"]');
        if (returnTo) {
          returnTo.value = currentPath();
        } else {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'return_to';
          input.value = currentPath();
          clone.appendChild(input);
        }

        placeholder.replaceWith(clone);

        // x-popover normally focuses the selected option on open. It opened
        // against an empty list, so move focus now if the popover is already up.
        var popover = clone.closest('x-popover');
        if (popover && popover.hasAttribute('open')) {
          var selected = clone.querySelector('[aria-selected="true"]');
          if (selected && typeof selected.focus === 'function') {
            selected.focus({ preventScroll: false });
          }
        }
      })
      .catch(function () {
        // Leave the placeholder in place and allow a retry on the next hover.
        placeholder.dataset.hgState = '';
      });
  }

  function wireCountrySelectors(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var toggles = scope.querySelectorAll('[data-hg-country-toggle]');

    Array.prototype.forEach.call(toggles, function (toggle) {
      if (toggle.dataset.hgWired) return;
      toggle.dataset.hgWired = '1';

      var wrapper = toggle.closest('.relative') || toggle.parentElement;
      if (!wrapper) return;

      var load = function () {
        hydrateCountryList(wrapper.querySelector('[data-hg-country-list]'));
      };

      ['pointerenter', 'focus', 'touchstart', 'click'].forEach(function (eventName) {
        toggle.addEventListener(eventName, load, { passive: true });
      });
    });
  }

  /* ====================================================================
   * 2. ABOUT-PAGE VIDEO POPUP
   * Moved from theme.liquid unchanged apart from being wrapped in a
   * function so it can be re-run after a section re-render in the editor.
   * ==================================================================== */
  function wireAboutVideo(root) {
    var scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll('.customAbout-videoWrapper').forEach(function (wrapper) {
      if (wrapper.dataset.hgWired) return;
      wrapper.dataset.hgWired = '1';

      wrapper.addEventListener('click', function () {
        var videoUrl = (wrapper.getAttribute('data-video') || '').trim();
        var popup = document.getElementById('customAboutPopup');
        var iframe = document.getElementById('customAboutIframe');
        if (!popup || !iframe || !videoUrl) return;

        var videoId;

        if (videoUrl.indexOf('youtube.com/watch?v=') !== -1) {
          videoId = videoUrl.split('v=')[1].split('&')[0];
          videoUrl = 'https://www.youtube.com/embed/' + videoId;
        }

        if (videoUrl.indexOf('youtu.be/') !== -1) {
          videoId = videoUrl.split('youtu.be/')[1];
          videoUrl = 'https://www.youtube.com/embed/' + videoId;
        }

        if (videoUrl.indexOf('youtube.com/shorts/') !== -1) {
          videoId = videoUrl.split('shorts/')[1].split('?')[0];
          videoUrl = 'https://www.youtube.com/embed/' + videoId;
        }

        if (videoUrl.indexOf('vimeo.com/') !== -1 && videoUrl.indexOf('player.vimeo.com') === -1) {
          videoId = videoUrl.split('vimeo.com/')[1].split('/')[0];
          videoUrl = 'https://player.vimeo.com/video/' + videoId;
        }

        iframe.src = videoUrl;
        popup.style.display = 'flex';
      });
    });

    scope.querySelectorAll('.customAbout-close').forEach(function (closeButton) {
      if (closeButton.dataset.hgWired) return;
      closeButton.dataset.hgWired = '1';

      closeButton.addEventListener('click', function () {
        var popup = document.getElementById('customAboutPopup');
        var iframe = document.getElementById('customAboutIframe');
        if (iframe) iframe.src = '';
        if (popup) popup.style.display = 'none';
      });
    });
  }

  /* ====================================================================
   * 3. PDP RATING -> REVIEWS SCROLL
   * Moved from theme.liquid. FIX: the original called
   * reviewSection.scrollIntoView() with no null check, so clicking the
   * star rating on any product without a review widget threw
   * "Cannot read properties of null".
   * ==================================================================== */
  var REVIEW_WIDGET_SELECTOR =
    '.jdgm-widget, .judgeme-main-widget, .yotpo-main-widget, .loox-rating, #shopify-product-reviews, .spr-reviews';

  function wireRatingScroll() {
    setTimeout(function () {
      document.querySelectorAll('.shopify-section--main-product').forEach(function (section) {
        var ratings = section.querySelectorAll('[data-block-type="rating"], .rating-badge');

        ratings.forEach(function (rating) {
          if (rating.dataset.hgWired) return;
          rating.dataset.hgWired = '1';
          rating.style.cursor = 'pointer';

          rating.addEventListener('click', function () {
            var reviewSection = document.querySelector(REVIEW_WIDGET_SELECTOR);
            if (!reviewSection) return;
            reviewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        });
      });
    }, 1000);
  }

  /* ====================================================================
   * 5. EPA CART DRAWER PRICE FIX (defined before 4, which re-attaches it)
   * Moved from theme.liquid. Behaviour is identical; the source was
   * reformatted (the original had runaway indentation costing a few KB per
   * page) and the observer is now re-attached when the drawer is replaced,
   * which previously silently stopped the fix from running.
   * ==================================================================== */
  var epaObserver = null;

  function fixEpaDrawerPrices() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    drawer.querySelectorAll('.spicegems-cart-discount.sg-ajax-line-price').forEach(function (discEl) {
      var srOnly = discEl.closest('.sr-only');
      if (!srOnly) return;

      var salePrice = srOnly.parentElement;
      if (!salePrice || salePrice.querySelector('.spa-fixed-discount')) return;

      var discMoney = discEl.querySelector('.money') || discEl;
      var discText = discMoney.textContent.trim();
      if (!discText) return;

      var origNode = Array.prototype.find.call(salePrice.childNodes, function (node) {
        return node.nodeType === 3 && node.textContent.trim();
      });

      if (origNode) {
        var struck = document.createElement('s');
        struck.className = 'spa-original-price text-subdued';
        struck.style.cssText = 'font-size:0.85em;opacity:0.7;margin-left:6px;';
        struck.textContent = origNode.textContent.trim();
        origNode.replaceWith(struck);
      }

      var visible = document.createElement('span');
      visible.className = 'spa-fixed-discount money';
      visible.textContent = discText;
      srOnly.after(visible);
    });
  }

  function attachEpaObserver() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    if (epaObserver) epaObserver.disconnect();

    fixEpaDrawerPrices();
    epaObserver = new MutationObserver(fixEpaDrawerPrices);
    epaObserver.observe(drawer, { childList: true, subtree: true });
  }

  /* ====================================================================
   * 4. SPICE ADD-ON CART DRAWER SYNC
   * Moved from theme.liquid. Behaviour unchanged, except that refreshing
   * the drawer HTML now re-attaches the EPA observer above.
   * ==================================================================== */
  var CART_DRAWER_OPENER = 'a[aria-controls="cart-drawer"]';

  function safeOpenDrawer() {
    var opener = document.querySelector(CART_DRAWER_OPENER);
    if (opener) {
      opener.click();
      return true;
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.setAttribute('open', '');
    return true;
  }

  function refreshDrawerHTML() {
    return fetch('/?sections=cart-drawer')
      .then(function (response) {
        return response.json();
      })
      .then(function (json) {
        var html = json['cart-drawer'];
        if (!html) {
          console.warn('cart-drawer section missing');
          return;
        }

        // Same reason as the country list above: DOMParser would leave the
        // drawer's lazy line-item thumbnails permanently unloaded.
        var range = document.createRange();
        range.selectNode(document.body);
        var fragment = range.createContextualFragment(html);

        var newDrawer = fragment.querySelector('#cart-drawer');
        var oldDrawer = document.querySelector('#cart-drawer');

        if (newDrawer && oldDrawer) {
          oldDrawer.replaceWith(newDrawer);
          // The old node carried the MutationObserver; rebind to the new one.
          attachEpaObserver();
        }
      })
      .catch(function (error) {
        console.error('Drawer HTML refresh error:', error);
      });
  }

  function afterAddon() {
    fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function () {
        return refreshDrawerHTML();
      })
      .then(function () {
        setTimeout(safeOpenDrawer, 150);
      });
  }

  function wrapSpice() {
    if (!(window.SPICEADDONS && typeof window.SPICEADDONS.individualAddonSubmit === 'function')) {
      return false;
    }
    if (window.SPICEADDONS.__hgWrapped) return true;

    var original = window.SPICEADDONS.individualAddonSubmit;
    window.SPICEADDONS.__hgWrapped = true;

    window.SPICEADDONS.individualAddonSubmit = function (button) {
      var result = original.call(this, button);

      if (result && typeof result.then === 'function') {
        result.then(function () {
          setTimeout(afterAddon, 200);
        });
      } else {
        setTimeout(afterAddon, 100);
      }

      return result;
    };

    return true;
  }

  function pollForSpice() {
    if (wrapSpice()) return;

    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (wrapSpice() || tries > 12) clearInterval(timer);
    }, 300);
  }

  /* ==================================================================== */

  function init(root) {
    wireCountrySelectors(root);
    wireAboutVideo(root);
  }

  function boot() {
    init(document);
    wireRatingScroll();
    attachEpaObserver();
    pollForSpice();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-wire whatever was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    if (event.target && event.target.querySelector('#cart-drawer')) attachEpaObserver();
  });
})();
